import React, { useMemo, useState } from 'react';
import { TopBar } from '../../components/TopBar/TopBar';
import { Project } from '../../shared/models/Project';
import { showError } from '../../utils/errorHandler';
import './StartDonationTeamView.css';

interface StartDonationTeamViewProps {
  project: Project;
  onBack: () => void;
  onProjectClick?: () => void;
  teamAvatar?: string;
  onSubmit: (payload: { name: string; slogan?: string; targetAmount?: number }) => Promise<void>;
}

const placeholder = '/placeholder.svg';

export const StartDonationTeamView: React.FC<StartDonationTeamViewProps> = ({ project, onBack, onProjectClick, teamAvatar, onSubmit }) => {
  const [name, setName] = useState('');
  const [slogan, setSlogan] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(() => {
    return !!name.trim() && !!slogan.trim() && !submitting;
  }, [name, slogan, submitting]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const trimmedSlogan = slogan.trim();
    if (!trimmedName) {
      showError('请输入团队名称', 'StartDonationTeamView.handleSubmit');
      return;
    }
    if (trimmedName.length > 20) {
      showError('团队名称最多20个字', 'StartDonationTeamView.handleSubmit');
      return;
    }
    if (trimmedSlogan.length > 30) {
      showError('团队口号最多30个字', 'StartDonationTeamView.handleSubmit');
      return;
    }
    if (!trimmedSlogan) {
      showError('请输入团队口号', 'StartDonationTeamView.handleSubmit');
      return;
    }

    setSubmitting(true);
    try {
      const parsedTargetAmount = Number(targetAmount);
      await onSubmit({
        name: trimmedName,
        slogan: trimmedSlogan || undefined,
        targetAmount: targetAmount.trim() === '' || Number.isNaN(parsedTargetAmount) ? undefined : parsedTargetAmount,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="start-team-view">
      <TopBar title="一起捐" showBack onBack={onBack} />

      <div className="start-team-tip">
        请填写活动的基本信息，发起一起捐，号召更多的爱心人士加入
      </div>

      <div className="start-team-avatar-row">
        {teamAvatar ? (
          <img className="start-team-avatar" src={teamAvatar} alt="团队头像" onError={(e) => { e.currentTarget.src = placeholder; }} />
        ) : (
          <div className="start-team-avatar start-team-avatar-fallback">👥</div>
        )}
      </div>

      <div className="start-team-card">
        <div className="start-team-field">
          <label className="start-team-label">
            <span className="start-team-required">*</span>
            <span>团队名称</span>
          </label>
          <input
            className="start-team-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="请输入"
            maxLength={20}
          />
        </div>

        <div className="start-team-field">
          <label className="start-team-label">
            <span className="start-team-required">*</span>
            <span>团队口号</span>
          </label>
          <textarea
            className="start-team-input start-team-textarea"
            value={slogan}
            onChange={(e) => setSlogan(e.target.value)}
            placeholder="请输入口号（30字以内）"
            maxLength={30}
            rows={2}
          />
        </div>

        <div className="start-team-field">
          <label className="start-team-label">筹款目标</label>
          <input
            className="start-team-input"
            value={targetAmount}
            onChange={(e) => setTargetAmount(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="输入筹款目标，0或不填写则表示无上限"
          />
          <div className="start-team-helper">0或不填写则表示无上限。</div>
        </div>
      </div>

      <div className="start-team-project-section">
        <div className="start-team-section-title">
          <span>捐赠项目</span>
        </div>
        <div className="start-team-project-item clickable" onClick={() => onProjectClick?.()}>
          <img
            src={project.coverThumbUrl || project.coverImageUrl || placeholder}
            alt={project.title}
            className="start-team-project-cover"
            onError={(e) => {
              e.currentTarget.src = placeholder;
            }}
          />
          <div className="start-team-project-content">
            <div className="start-team-project-name">{project.title}</div>
            <div className="start-team-project-desc">{project.description || '暂无简介'}</div>
          </div>
          <div className="start-team-project-arrow">›</div>
        </div>
      </div>

      <div className="start-team-bottom">
        <button className="start-team-submit-btn" disabled={!canSubmit} onClick={handleSubmit}>
          {submitting ? '发起中...' : '发起一起捐'}
        </button>
      </div>
    </div>
  );
};

